import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { useGroupGet } from "hooks/citajteo/dl_group/useGroupGet";
import { useGroupUpdate } from "hooks/citajteo/dl_group/useGroupUpdate";
import { QUERY_KEYS } from "constant/react_query";
import toast from "react-hot-toast";

type DlGroupFormState = {
  slug: string;
  fCondText: string;
  groupsText: string;
};

const EMPTY_FORM: DlGroupFormState = {
  slug: "",
  fCondText: "{}",
  groupsText: "",
};

const buildFormFromGroup = (group: any): DlGroupFormState => ({
  slug: group.slug ?? "",
  fCondText: JSON.stringify(group.fCond ?? {}, null, 2),
  groupsText: Array.isArray(group.groups) ? group.groups.join(", ") : "",
});

const DlGroupEdit: React.FC = () => {
  const navigate = useNavigate();
  const { itemId = "" } = useParams<{ itemId: string }>();
  const group = useGroupGet(itemId);
  const { groupUpdateMutate } = useGroupUpdate({
    invalidateKeysArray: [
      [QUERY_KEYS.CITAJTEO_DL_GROUP_SEARCH],
      [QUERY_KEYS.CITAJTEO_DL_GROUP_GET, itemId],
    ],
  });
  const [form, setForm] = useState<DlGroupFormState>(EMPTY_FORM);
  const [errorText, setErrorText] = useState("");

  useEffect(() => {
    if (group) {
      setForm(buildFormFromGroup(group));
    }
  }, [group]);

  const handleChange =
    (field: keyof DlGroupFormState) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((currentForm) => ({
        ...currentForm,
        [field]: event.target.value,
      }));
    };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorText("");

    const trimmedSlug = form.slug.trim();

    if (trimmedSlug === "") {
      setErrorText("Slug ne sme biti prazan.");
      return;
    }

    let parsedFCond: Record<string, string> = {};
    let parsedGroups: string[] = [];

    try {
      const rawFCond = form.fCondText.trim();
      parsedFCond = rawFCond === "" ? {} : JSON.parse(rawFCond);
    } catch (error) {
      setErrorText("fCond mora biti ispravan JSON objekat.");
      return;
    }

    if (
      typeof parsedFCond !== "object" ||
      parsedFCond === null ||
      Array.isArray(parsedFCond)
    ) {
      setErrorText("fCond mora biti JSON objekat.");
      return;
    }

    const rawGroups = form.groupsText.trim();
    parsedGroups =
      rawGroups === ""
        ? []
        : rawGroups
            .split(",")
            .map((groupItem) => groupItem.trim())
            .filter((groupItem) => groupItem !== "");

    const updatePayload = {
      slug: trimmedSlug,
      fCond: parsedFCond,
      groups: parsedGroups,
    };

    groupUpdateMutate(
      { id: itemId, updatePayload },
      {
        onSuccess: () => {
          toast.success("Uspešno ažuriranje odrednice.");
          navigate("/admin-dashboard/dl-group-li");
        },
        onError: (error) => {
          console.error("Error updating group:", error);
          toast.error("Greška prilikom ažuriranja odrednice.");
        },
      },
    );
  };

  const handleReset = () => {
    if (group) {
      setForm(buildFormFromGroup(group));
    } else {
      setForm(EMPTY_FORM);
    }
    setErrorText("");
  };

  if (!group) {
    return <FullWidth>Učitavanje odrednice...</FullWidth>;
  }

  return (
    <FullWidth>
      <div className="App">
        <Form onSubmit={handleSubmit}>
          <Title>Uredi odrednicu</Title>

          <Field>
            <label htmlFor="slug">Slug</label>
            <Input
              id="slug"
              type="text"
              value={form.slug}
              onChange={handleChange("slug")}
            />
          </Field>

          <Field>
            <label htmlFor="fCond">fCond JSON</label>
            <TextArea
              id="fCond"
              rows={8}
              value={form.fCondText}
              onChange={handleChange("fCondText")}
            />
          </Field>

          <Field>
            <label htmlFor="groups">Groups</label>
            <Input
              id="groups"
              type="text"
              value={form.groupsText}
              onChange={handleChange("groupsText")}
              placeholder="group-1, group-2"
            />
          </Field>

          {errorText !== "" && <ErrorText>{errorText}</ErrorText>}

          <Actions>
            <Button type="submit">OK</Button>
            <Button type="button" onClick={handleReset}>
              Reset
            </Button>
          </Actions>
        </Form>
      </div>
    </FullWidth>
  );
};

export default DlGroupEdit;

const FullWidth = styled.div`
  width: 100%;
`;

const Form = styled.form`
  width: 100%;
  max-width: 900px;
`;

const Title = styled.h2`
  margin: 0 0 1rem;
`;

const Field = styled.div`
  margin-bottom: 1rem;

  label {
    display: block;
    margin-bottom: 0.35rem;
    font-weight: 600;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.5rem 0.65rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.5rem 0.65rem;
  resize: vertical;
`;

const ErrorText = styled.div`
  margin-bottom: 1rem;
  color: #b00020;
  font-weight: 600;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const Button = styled.button`
  padding: 0.4rem 0.9rem;
  cursor: pointer;
`;
